import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, pairwise, startWith } from 'rxjs';
import { LanguageService } from './language.service';
import { AppRoutePath, getTranslatedRoutePath } from '../route-translation';
import { isHotelDetailsRoute } from '../helpers';

@Injectable({ providedIn: 'root' })
export class ScrollService {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly router = inject(Router);
	private readonly languageService = inject(LanguageService);

	private readonly isBrowser = isPlatformBrowser(this.platformId);

	public initializeScrollBehavior(): void {
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				startWith(null),
				pairwise(),
				takeUntilDestroyed()
			)
			.subscribe(([previous, current]: [NavigationEnd | null, NavigationEnd | null]) => {
				if (current && this.shouldScrollToTop(previous, current)) {
					this.scrollToTop();
				}
			});
	}

	private shouldScrollToTop(previous: NavigationEnd | null, current: NavigationEnd): boolean {
		if (!previous) {
			return true;
		} // First navigation

		const currentPath = current.url.split('?')[0];
		const previousPath = previous.url.split('?')[0];

		if (this.isExcludedRoute(currentPath) && currentPath === previousPath) {
			return false; // Don't scroll - only query params changed on excluded route
		}

		// For all other cases, scroll to top
		return true;
	}

	private isExcludedRoute(path: string): boolean {
		return isHotelDetailsRoute(path, this.languageService.getSelectedLanguage());
	}

	private scrollToTop(): void {
		if (!this.isBrowser) {
			return;
		}
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth', // Change to 'auto' for instant scroll
		});
	}

	public forceScrollToTop(): void {
		this.scrollToTop();
	}
}
